import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Database, RefreshCw, CheckCircle2, XCircle, ChevronDown, ChevronRight,
  Server, Layers, Users, AlertCircle, Activity,
} from 'lucide-react';
import { api } from '../lib/api';

// ── API types (match what api-gateway returns) ─────────────────────────────

interface ClusterOverview {
  healthy: boolean;
  broker_count: number;
  brokers: Array<{ id: number; address: string; is_alive: boolean; version: string }>;
}

interface Partition {
  id: number;
  leader_id: number;
  high_watermark: number | null;
}

interface TopicInfo {
  name: string;
  partition_count: number;
  replication_factor: number;
  total_high_watermark: number;
  partitions: Partition[];
}

interface ConsumerGroupSummary {
  group_id: string;
  state: string;
  member_count: number;
  total_lag: number;
  topics: Array<{ topic: string; lag: number }>;
}

// ── Data fetchers ───────────────────────────────────────────────────────────

const fetchOverview = () => api.get<ClusterOverview>('/redpanda/overview').then((r) => r.data);
const fetchTopics = () => api.get<TopicInfo[]>('/redpanda/topics').then((r) => r.data);
const fetchGroups = () => api.get<ConsumerGroupSummary[]>('/redpanda/consumer-groups').then((r) => r.data);

// ── Small shared components ─────────────────────────────────────────────────

function StatusBadge({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md border ${
      ok
        ? 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20'
        : 'text-red-400 bg-red-400/10 border-red-400/25'
    }`}>
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      {label}
    </span>
  );
}

function StateBadge({ state }: { state: string }) {
  const map: Record<string, string> = {
    Stable:     'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
    Empty:      'text-slate-400 bg-slate-400/10 border-slate-500/20',
    Dead:       'text-red-400 bg-red-400/10 border-red-400/25',
    PreparingRebalance: 'text-amber-400 bg-amber-400/10 border-amber-400/20',
    CompletingRebalance: 'text-amber-400 bg-amber-400/10 border-amber-400/20',
  };
  return (
    <span className={`inline-flex text-[10px] font-semibold px-2 py-0.5 rounded-md border ${map[state] ?? 'text-slate-400 bg-slate-400/10 border-slate-500/20'}`}>
      {state}
    </span>
  );
}

function SectionHeader({ icon: Icon, title, count }: { icon: React.ElementType; title: string; count?: number }) {
  return (
    <div className="flex items-center gap-2.5 mb-4">
      <Icon className="w-4 h-4 text-cyan-400" />
      <h2 className="text-sm font-semibold text-slate-200">{title}</h2>
      {count !== undefined && (
        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-800 text-slate-500 border border-slate-700/50">
          {count}
        </span>
      )}
    </div>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <div className="card p-4 flex items-start gap-2.5 text-red-400">
      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <span className="text-xs">{message}</span>
    </div>
  );
}

function Skeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="card p-5 space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-4 rounded bg-slate-800 animate-pulse" style={{ width: `${65 + (i % 3) * 12}%` }} />
      ))}
    </div>
  );
}

// ── Cluster Overview section ────────────────────────────────────────────────

function ClusterSection({ refetchAll }: { refetchAll: () => void }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['rp-overview'],
    queryFn: fetchOverview,
    refetchInterval: 30_000,
  });

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <SectionHeader icon={Server} title="Cluster" />
        <button
          onClick={refetchAll}
          className="flex items-center gap-1.5 text-[11px] text-slate-500 hover:text-cyan-400 transition-colors"
        >
          <RefreshCw className="w-3 h-3" />
          Refresh all
        </button>
      </div>

      {isLoading && <Skeleton rows={2} />}
      {isError && <ErrorCard message={(error as Error).message} />}
      {data && (
        <motion.div
          className="card p-5"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                data.healthy
                  ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.6)]'
                  : 'bg-red-400 shadow-[0_0_8px_rgba(248,113,113,0.6)]'
              }`} />
              <span className="text-sm font-semibold text-slate-200">Redpanda</span>
              <StatusBadge ok={data.healthy} label={data.healthy ? 'HEALTHY' : 'DEGRADED'} />
            </div>
            <span className="text-xs text-slate-600 font-mono">{data.broker_count} broker{data.broker_count !== 1 ? 's' : ''}</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.brokers.map((b) => (
              <div key={b.id} className="rounded-lg bg-slate-900/60 border border-slate-800/60 p-3 flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${b.is_alive ? 'bg-emerald-400' : 'bg-red-400'}`} />
                    <span className="text-xs font-semibold text-slate-300">broker-{b.id}</span>
                  </div>
                  <span className="text-[11px] font-mono text-slate-600">{b.address}</span>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <StatusBadge ok={b.is_alive} label={b.is_alive ? 'ALIVE' : 'DOWN'} />
                  <span className="text-[10px] text-slate-700 font-mono">{b.version}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </section>
  );
}

// ── Topics section ──────────────────────────────────────────────────────────

function TopicRow({ topic }: { topic: TopicInfo }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr
        className="border-b border-slate-800/60 hover:bg-slate-800/30 cursor-pointer transition-colors"
        onClick={() => setExpanded((v) => !v)}
      >
        <td className="py-2.5 pl-4 pr-2">
          <div className="flex items-center gap-2">
            {expanded
              ? <ChevronDown className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />
              : <ChevronRight className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />}
            <span className="text-xs font-mono text-slate-300">{topic.name}</span>
          </div>
        </td>
        <td className="py-2.5 px-4 text-right text-xs text-slate-500">{topic.partition_count}</td>
        <td className="py-2.5 px-4 text-right text-xs text-slate-500">{topic.replication_factor}</td>
        <td className="py-2.5 pl-4 pr-4 text-right text-xs font-mono text-slate-400">
          {topic.total_high_watermark.toLocaleString()}
        </td>
      </tr>
      <AnimatePresence>
        {expanded && topic.partitions.length > 0 && (
          <tr>
            <td colSpan={4} className="p-0">
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="overflow-hidden"
              >
                <table className="w-full text-[11px] font-mono bg-slate-950/40">
                  <thead>
                    <tr className="text-slate-700 text-left">
                      <th className="py-1.5 pl-12 pr-4 font-normal">Partition</th>
                      <th className="py-1.5 px-4 text-right font-normal">Leader</th>
                      <th className="py-1.5 px-4 text-right font-normal">High Watermark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topic.partitions.map((p) => (
                      <tr key={p.id} className="border-t border-slate-800/40">
                        <td className="py-1.5 pl-12 pr-4 text-slate-600">{p.id}</td>
                        <td className="py-1.5 px-4 text-right text-slate-600">{p.leader_id}</td>
                        <td className="py-1.5 px-4 text-right text-slate-500">
                          {p.high_watermark !== null ? p.high_watermark.toLocaleString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </motion.div>
            </td>
          </tr>
        )}
      </AnimatePresence>
    </>
  );
}

function TopicsSection() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['rp-topics'],
    queryFn: fetchTopics,
    refetchInterval: 30_000,
  });

  return (
    <section>
      <SectionHeader icon={Layers} title="Topics" count={data?.length} />
      {isLoading && <Skeleton rows={4} />}
      {isError && <ErrorCard message={(error as Error).message} />}
      {data && (
        <motion.div
          className="card overflow-hidden"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800/80 bg-slate-900/40 text-left">
                <th className="py-2.5 pl-4 pr-2 text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Topic</th>
                <th className="py-2.5 px-4 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Partitions</th>
                <th className="py-2.5 px-4 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wider">RF</th>
                <th className="py-2.5 pl-4 pr-4 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Total Offset</th>
              </tr>
            </thead>
            <tbody>
              {data.map((t) => (
                <TopicRow key={t.name} topic={t} />
              ))}
              {data.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-6 text-center text-xs text-slate-700">No topics found</td>
                </tr>
              )}
            </tbody>
          </table>
        </motion.div>
      )}
    </section>
  );
}

// ── Consumer Groups section ─────────────────────────────────────────────────

function LagBadge({ lag }: { lag: number }) {
  if (lag === -1) return <span className="text-xs text-slate-600">—</span>;
  if (lag === 0) return <span className="text-xs font-mono text-emerald-400">0</span>;
  if (lag < 100) return <span className="text-xs font-mono text-amber-400">{lag.toLocaleString()}</span>;
  return <span className="text-xs font-mono text-red-400">{lag.toLocaleString()}</span>;
}

function GroupRow({ group }: { group: ConsumerGroupSummary }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr
        className="border-b border-slate-800/60 hover:bg-slate-800/30 cursor-pointer transition-colors"
        onClick={() => setExpanded((v) => !v)}
      >
        <td className="py-2.5 pl-4 pr-2">
          <div className="flex items-center gap-2">
            {expanded
              ? <ChevronDown className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />
              : <ChevronRight className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />}
            <span className="text-xs font-mono text-slate-300">{group.group_id}</span>
          </div>
        </td>
        <td className="py-2.5 px-4"><StateBadge state={group.state} /></td>
        <td className="py-2.5 px-4 text-right text-xs text-slate-500">{group.member_count}</td>
        <td className="py-2.5 pl-4 pr-4 text-right"><LagBadge lag={group.total_lag} /></td>
      </tr>
      <AnimatePresence>
        {expanded && group.topics.length > 0 && (
          <tr>
            <td colSpan={4} className="p-0">
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="overflow-hidden"
              >
                <table className="w-full text-[11px] font-mono bg-slate-950/40">
                  <thead>
                    <tr className="text-slate-700 text-left">
                      <th className="py-1.5 pl-12 pr-4 font-normal">Topic</th>
                      <th className="py-1.5 px-4 text-right font-normal">Lag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.topics.map((t) => (
                      <tr key={t.topic} className="border-t border-slate-800/40">
                        <td className="py-1.5 pl-12 pr-4 text-slate-500">{t.topic}</td>
                        <td className="py-1.5 px-4 text-right"><LagBadge lag={t.lag} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </motion.div>
            </td>
          </tr>
        )}
      </AnimatePresence>
    </>
  );
}

function ConsumerGroupsSection() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['rp-consumer-groups'],
    queryFn: fetchGroups,
    refetchInterval: 30_000,
  });

  return (
    <section>
      <SectionHeader icon={Users} title="Consumer Groups" count={data?.length} />
      {isLoading && <Skeleton rows={3} />}
      {isError && <ErrorCard message={(error as Error).message} />}
      {data && (
        <motion.div
          className="card overflow-hidden"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800/80 bg-slate-900/40 text-left">
                <th className="py-2.5 pl-4 pr-2 text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Group ID</th>
                <th className="py-2.5 px-4 text-[11px] font-semibold text-slate-600 uppercase tracking-wider">State</th>
                <th className="py-2.5 px-4 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Members</th>
                <th className="py-2.5 pl-4 pr-4 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Total Lag</th>
              </tr>
            </thead>
            <tbody>
              {data.map((g) => (
                <GroupRow key={g.group_id} group={g} />
              ))}
              {data.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-6 text-center text-xs text-slate-700">No consumer groups found</td>
                </tr>
              )}
            </tbody>
          </table>
        </motion.div>
      )}
    </section>
  );
}

// ── Page root ───────────────────────────────────────────────────────────────

export function RedpandaPage() {
  const [tick, setTick] = useState(0);

  // Bumping `tick` triggers all three queries to refetch via queryClient.invalidateQueries
  // but the cleaner pattern here is to just change the key — or use queryClient directly.
  // Instead, each section has its own refetchInterval and the header Refresh button
  // signals all three queries via queryClient.
  const { refetch: refetchOverview } = useQuery({
    queryKey: ['rp-overview'],
    queryFn: fetchOverview,
    refetchInterval: 30_000,
    enabled: false, // managed by ClusterSection
  });
  const { refetch: refetchTopics } = useQuery({
    queryKey: ['rp-topics'],
    queryFn: fetchTopics,
    refetchInterval: 30_000,
    enabled: false,
  });
  const { refetch: refetchGroups } = useQuery({
    queryKey: ['rp-consumer-groups'],
    queryFn: fetchGroups,
    refetchInterval: 30_000,
    enabled: false,
  });

  const refetchAll = () => {
    setTick((t) => t + 1);
    void refetchOverview();
    void refetchTopics();
    void refetchGroups();
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      {/* ── Page header ── */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="flex items-start justify-between gap-4"
      >
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <Database className="w-5 h-5 text-cyan-400" />
            <h1 className="text-lg font-bold text-slate-100">Redpanda</h1>
          </div>
          <p className="text-xs text-slate-600">
            Message broker monitoring — cluster health, topic offsets, and consumer-group lag.
            Auto-refreshes every 30 s.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="flex items-center gap-1.5 text-[11px] text-slate-700">
            <Activity className="w-3 h-3 text-cyan-400/60" />
            Port 7564
          </span>
        </div>
      </motion.div>

      {/* Three use separate useQuery instances so they load/fail independently */}
      <ClusterSection key={`cluster-${tick}`} refetchAll={refetchAll} />
      <TopicsSection key={`topics-${tick}`} />
      <ConsumerGroupsSection key={`groups-${tick}`} />
    </div>
  );
}
